'use client'
import { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { Typography } from 'components/Typography'
import useMobileNavState from 'hooks/useMobileNavState'
import useActiveNavItemIndex from 'hooks/useActiveNavItemIndex'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import useIsSsr from 'hooks/useIsSsr'
import styles from './Navigation.module.scss'
import FEATURE_TOGGLES from 'constants/featureToggles'
import { mainProductConfig } from 'constants/mainProductConfig'
import classNames from 'classnames'
import ChevronDown from 'components/Svgs/ChevronDown.js'

/**
 * @param {{
 *  currentPathname: string
 *  disabledRoutes: []
 *  items: []
 *  mainNavLabel: string
 *  title: string
 * }} props
 */
export default function SubNavMenu({
  currentPathname,
  disabledRoutes,
  disabledElements,
  items = [],
  mainNavLabel,
  title,
  subNavTitleClassName,
}) {
  const [, setMobileNavOpen] = useMobileNavState()
  const [, setActiveIndex] = useActiveNavItemIndex()
  const isSsr = useIsSsr()
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const [expandedSubItem, setExpandedSubItem] = useState(null)

  // When an item with a route is clicked, the mobile nav should be dismissed,
  // active item should be cleared, and the page should scroll to the top if the
  // render is happening in the browser
  const handleItemClick = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isMobile && !isSsr && window.scrollTo({ top: 0, behavior: 'instant' })
    setTimeout(() => {
      //Fix navigation only for Safari
      setMobileNavOpen()
      setActiveIndex()
    }, 50)
  }

  const toggleSubItem = (index) => {
    setExpandedSubItem(expandedSubItem === index ? null : index)
  }

  const SubNavItem = ({ label, description, testid, url, index }) => (
    <AchieveLink
      href={url}
      withNextLink
      track={{
        list_name: 'Header Nav Link Click',
        mega_menu_tab: mainNavLabel,
        mega_menu_column: title,
        mega_menu_link: label,
        click_id: label,
        click_position: index + 1,
        click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${label}`,
        nav_link_section: 'Mega Menu',
        track_event: 'mega_menu',
        event_action: 'link_click',
        event_type: 'link_click',
      }}
    >
      {/* Have to use a regular a tag since AchieveLink can't forwardRef to a
      functional component so AscendLink doesn't work. */}
      <span
        className={styles['sub-nav-link']}
        data-selected={currentPathname === url}
        onClick={() => {
          handleItemClick()
        }}
        data-testid={testid ? testid : `header-menu-${index}`}
        data-nav="sub-nav"
      >
        <Typography
          component="div"
          className={classNames('bodyMd lg:text-[20px]! lg:font-bold!', subNavTitleClassName)}
        >
          {label}
        </Typography>
        {!isMobile && (
          <Typography
            className={styles['sub-nav-link-description']}
            variant="bodySm"
            fontWeight="normal"
            component="div"
          >
            {description}
          </Typography>
        )}
      </span>
    </AchieveLink>
  )

  const SubNavToggleItem = ({ label, testid, subItems, index }) => (
    <>
      <div
        className={classNames(styles['sub-nav-link'], 'cursor-pointer')}
        onClick={() => toggleSubItem(index)}
        data-testid={testid ? testid : `header-menu-${index}`}
      >
        <div className="flex items-center justify-between w-full">
          <Typography
            component="div"
            className={classNames(
              'bodyMd lg:text-[20px]! font-medium! lg:font-bold!',
              subNavTitleClassName
            )}
          >
            {label}
          </Typography>
          <span
            className={classNames(
              'transition-transform duration-200',
              expandedSubItem === index ? 'rotate-180' : ''
            )}
          >
            <ChevronDown title={label} width={13} height={8} />
          </span>
        </div>
      </div>
      {/* Nested sub-items */}
      <ul
        className={classNames(
          'pl-4 overflow-hidden transition-all duration-300',
          expandedSubItem === index ? 'max-h-[1000px]' : 'max-h-0'
        )}
      >
        {subItems.map((subItem, subIndex) => (
          <li key={subItem.label}>
            <AchieveLink
              href={subItem.url}
              withNextLink
              track={{
                list_name: 'Header Nav Link Click',
                mega_menu_tab: mainNavLabel,
                mega_menu_column: title,
                mega_menu_link: subItem.label,
                click_id: subItem.label,
                click_position: subIndex + 1,
                click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
                  subItem.label
                }`,
                nav_link_section: 'Mega Menu',
                track_event: 'mega_menu',
                event_action: 'link_click',
                event_type: 'link_click',
              }}
            >
              <span
                className={styles['sub-nav-link']}
                data-selected={currentPathname === subItem.url}
                onClick={() => {
                  handleItemClick()
                }}
                data-testid={subItem.testid ? subItem.testid : `header-menu-${index}-${subIndex}`}
                data-nav="sub-nav-nested"
              >
                <Typography
                  component="div"
                  className={classNames('bodyMd font-normal! ', subNavTitleClassName)}
                >
                  {subItem.label}
                </Typography>
              </span>
            </AchieveLink>
          </li>
        ))}
      </ul>
    </>
  )

  return (
    <div
      className={
        styles[
          `sub-nav-menu${
            disabledElements[FEATURE_TOGGLES[siteEnv].ACX_WEB_ENABLE_NAV_TITLES]
              ? ''
              : '-without-subtitle'
          }`
        ]
      }
      data-nav="container"
    >
      {disabledElements[FEATURE_TOGGLES[siteEnv].ACX_WEB_ENABLE_NAV_TITLES] && (
        <div className={styles['menu-title-container']}>
          <Typography
            variant="bodySm"
            fontWeight="bold"
            component="div"
            className={styles['menu-title']}
          >
            {title}
          </Typography>
        </div>
      )}
      <nav role="navigation">
        <ul>
          {items.map(({ label, description, testid, url = '#', subItems }, index) => (
            <Fragment key={label}>
              {!disabledRoutes.includes(url) && (
                <li>
                  {subItems && subItems.length > 0 && isMobile ? (
                    <SubNavToggleItem
                      label={label}
                      testid={testid}
                      subItems={subItems}
                      index={index}
                    />
                  ) : (
                    <SubNavItem
                      label={label}
                      description={description}
                      testid={testid}
                      url={url}
                      index={index}
                    />
                  )}
                </li>
              )}
            </Fragment>
          ))}
        </ul>
      </nav>
    </div>
  )
}

SubNavMenu.propTypes = {
  currentPathname: PropTypes.string.isRequired,
  disabledRoutes: PropTypes.array,
  mainNavLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  items: PropTypes.array,
}
